import { KeyMeasure, TKeyMeasure } from '../api/analytics/KeyMeasure';
import React from 'react';
import { ITabNavigationItem } from '../layout/PageTabNavigationItems';
import { OptionsBuilderItemTypes } from '../types/dataframes/options-builder-item-types';
import { TDimension } from '../api/analytics/Dimension';
import { KeyMeasureFact, TKeyMeasureFact } from '../api/analytics/KeyMeasureFact';
import { TDimensionAttribute } from '../api/analytics/DimensionAttribute';
import { DropdownItem } from '@patternfly/react-core';
import { KeyMeasureFactOptions } from '../types/dataframes/dataframe-key-measure-fact-options';
import { DimensionOptions } from '../types/dataframes/dataframe-dimension-options';
import {
	TDataframe,
	TDataframeData,
	TDataframeEntry,
	TDataframeFilter,
	TDataframeOrder,
} from '../api/types';
import {
	AvailableItems,
	DraggableMenuItemConfig,
	DraggableMenuItemData,
} from '../types/databuilder/databuilder';
import { generateGUID } from '../helpers/guid.helper';
import { DataBuilderZones } from '../enums/data-builder-zones';
import { TFolder } from '../api/types/folder';
import {
	TNewDataframe,
	TNewDataframeData,
	TNewDataframeEntry,
	TNewDataframeFilter,
	TNewDataframeOrder,
} from '../api/dataframes/Dataframes';
import { MetaDataFrame } from '../enums/meta-dataframe-name.enum';
import { generateRandomString } from '../helpers/random-string.helper';
import { TUnitType } from '../api/analytics/UnitType';
import { isUndefined, uniq } from 'lodash';
import { DashboardFilter } from '../api/dashbboards/DashboardFilter';
import { defaultDateRangeDim } from '../constants/default-date-range-dim.constant';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

export type LabeledDraggable = {
	label: string;
	items: ITabNavigationItem[];
};

export const useFactOptions = (
	isCalculatedFact = false,
	disableCalculatedFact = false,
	data: DraggableMenuItemConfig | null,
	hideConfigureCalculationOption?: boolean,
	showConditionalFormattingOption?: boolean,
	clearFilter?: () => void
): JSX.Element[] => {
	let dropdownItems = [
		<DropdownItem
			id={
				disableCalculatedFact
					? KeyMeasureFactOptions.CalculationInvalid
					: KeyMeasureFactOptions.Calculation
			}
			key={KeyMeasureFactOptions.Calculation}
			tooltip={
				disableCalculatedFact
					? 'You have newly dropped Facts, please save the Dataframe first before configuring Calculated Facts'
					: 'Configure Calculation'
			}
			className={disableCalculatedFact ? 'disabled-dropdown-item' : ''}
		>
			Configure Calculation
		</DropdownItem>,
		<DropdownItem
			id={KeyMeasureFactOptions.ChangeUnitType}
			key={KeyMeasureFactOptions.ChangeUnitType}
			tooltip="Change Unit Type"
		>
			Change Unit Type
		</DropdownItem>,
		<DropdownItem
			id={KeyMeasureFactOptions.Filters}
			key={KeyMeasureFactOptions.Filters}
			tooltip="Filters"
		>
			Filters
		</DropdownItem>,
		<DropdownItem
			className={` ${!data?.value ? 'hidden' : ''}`}
			tooltip="Clear Filter"
			onClick={() => {
				clearFilter && clearFilter();
			}}
		>
			<span>
				Clear Filter
				<FontAwesomeIcon
					className="clear-filter-icon"
					icon={faTimes}
				/>
			</span>
		</DropdownItem>,
		<DropdownItem
			id={KeyMeasureFactOptions.ConditionalFormatting}
			key={KeyMeasureFactOptions.ConditionalFormatting}
			tooltip="Conditional Formatting"
		>
			Conditional Formatting
		</DropdownItem>,
		<DropdownItem
			id={KeyMeasureFactOptions.Options}
			key={KeyMeasureFactOptions.Options}
			tooltip="Options"
		>
			Options
		</DropdownItem>,
		<DropdownItem
			id={KeyMeasureFactOptions.RemoveFact}
			key={KeyMeasureFactOptions.RemoveFact}
			tooltip="Remove"
		>
			Remove
		</DropdownItem>,
	];

	if (isCalculatedFact == false || hideConfigureCalculationOption) {
		dropdownItems = dropdownItems.filter((x) => x.key != KeyMeasureFactOptions.Calculation);
	}

	if (!showConditionalFormattingOption) {
		dropdownItems = dropdownItems.filter(
			(x) => x.key != KeyMeasureFactOptions.ConditionalFormatting
		);
	}

	return dropdownItems;
};

export const useDimensionOptions = (
	item?: DraggableMenuItemData,
	breakoutGroups?: boolean,
	hasDrilldown?: boolean
): JSX.Element[] => {
	return [
		<DropdownItem
			id={DimensionOptions.Options}
			key={DimensionOptions.Options}
			tooltip="Options"
		>
			Options
		</DropdownItem>,
		...(breakoutGroups && item
			? [
					<DropdownItem
						id={DimensionOptions.BreakoutGroups}
						key={DimensionOptions.BreakoutGroups}
						tooltip={
							item.data?.totaled
								? 'Remove from Breakout Groups'
								: 'Add to Breakout Groups'
						}
					>
						{item.data?.totaled
							? 'Remove from Breakout Groups'
							: 'Add to Breakout Groups'}
					</DropdownItem>,
			  ]
			: []),
		...(hasDrilldown && item
			? [
					<DropdownItem
						id={DimensionOptions.EnableDrilldown}
						key={DimensionOptions.EnableDrilldown}
						tooltip={item.data?.drillable ? 'Disable Drilldown' : 'Enable Drilldown'}
					>
						{item.data?.drillable ? 'Disable Drilldown' : 'Enable Drilldown'}
					</DropdownItem>,
			  ]
			: []),
		<DropdownItem
			id={DimensionOptions.RemoveDimension}
			key={DimensionOptions.RemoveDimension}
			tooltip="Remove"
		>
			Remove
		</DropdownItem>,
	];
};

export const populateDataForFact = (
	fact: TKeyMeasureFact,
	unitType?: TUnitType
): DraggableMenuItemConfig => {
	return {
		id: fact.id,
		title: fact.name,
		unitType: fact.unitType as number,
		numDecimals:
			unitType?.precision !== undefined && unitType?.precision !== null
				? unitType.precision
				: 2,
		defaultWhenNull: '***',
		hidden: false,
		isVisible: true,
		totaled: false,
	};
};

export const populateDataForCalcFact = (
	fact: TDataframeData,
	unitType?: TUnitType
): DraggableMenuItemConfig => {
	return {
		id: fact.id,
		title: fact.title ?? fact.alias ?? '',
		unitType: fact.unitType as number,
		numDecimals: unitType?.precision ? unitType.precision : 2,
		defaultWhenNull: '***',
		hidden: false,
		isVisible: true,
		totaled: false,
		alias: fact.alias,
		calculated: true,
		formula_operation: fact.formula_operation,
		formula_variable_1: fact.formula_variable_1,
		formula_variable_2: fact.formula_variable_2,
		minValue: fact.min_value ?? undefined,
		maxValue: fact.max_value ?? undefined,
	};
};

export const populateDataForDimension = (dimension: TDimension): DraggableMenuItemConfig => {
	return {
		id: dimension.id,
		title: dimension.name ?? dimension.display_name,
		useSequence: false,
		drillable: false,
		isVisible: true,
		totaled: false,
	};
};

export const populateDataForDimensionAttribute = (
	attr: TDimensionAttribute | TDimension
): DraggableMenuItemConfig => {
	return {
		id: attr.id,
		title: attr.name,
		useSequence: false,
		drillable: false,
		isVisible: true,
		totaled: false,
	};
};

export const populateDataForCalculatedFact = (
	measure: TKeyMeasure,
	unitType?: TUnitType
): DraggableMenuItemConfig => {
	return {
		id: undefined,
		title: measure.display_name ?? measure.name,
		unitType: measure.unitType as number,
		numDecimals: unitType?.precision ? unitType.precision : 2,
		defaultWhenNull: '***',
		hidden: false,
		isVisible: true,
		totaled: false,
	};
};

const resolveFormulaVariableId = (
	dataset: TDataframeData,
	field: 'formula_variable_1' | 'formula_variable_2'
): number | undefined => {
	const direct = dataset[field];
	if (typeof direct === 'number') {
		return direct;
	}

	const camelField = field === 'formula_variable_1' ? 'formulaVariable1' : 'formulaVariable2';
	const nested = (dataset as Record<string, unknown>)[camelField];
	if (nested && typeof nested === 'object' && 'id' in nested) {
		return (nested as { id: number }).id;
	}

	return undefined;
};

export const normalizeDataframeDatasets = (datasets: TDataframeData[]): TDataframeData[] =>
	datasets.map((dataset) => ({
		...dataset,
		formula_variable_1: resolveFormulaVariableId(dataset, 'formula_variable_1'),
		formula_variable_2: resolveFormulaVariableId(dataset, 'formula_variable_2'),
	}));

export const populateDroppedFactsByDataframe = (
	dataframe: TDataframe,
	measures: TKeyMeasure[]
): DraggableMenuItemData[] => {
	const factsToSet: DraggableMenuItemData[] = [];

	dataframe.datasets.forEach((_: TDataframeData) => {
		let factToUse = {};

		measures.forEach((measure) => {
			const fact = measure.keyMeasureFacts.find((fact) => fact.id == _.keyMeasureFact);
			if (fact) {
				factToUse = fact;
			}
		});

		const factFilter = dataframe.filters.find(
			(filter) =>
				filter.entity_id == _.keyMeasureFact &&
				filter.entity_type === OptionsBuilderItemTypes.KeyMeasureFact
		);

		factsToSet.push({
			id: generateGUID(),
			entity: factToUse,
			data: {
				id: _.calculated ? _.id : _.keyMeasureFact,
				title: _.title ?? '',
				unitType: _.unitType as number,
				numDecimals: _.numDecimals,
				defaultWhenNull: _.defaultWhenNULL ?? '***',
				hidden: _.hidden,
				isVisible: true,
				totaled: false,
				alias: _.alias,
				calculated: _.calculated,
				formula_operation: _.formula_operation,
				formula_variable_1: resolveFormulaVariableId(_, 'formula_variable_1'),
				formula_variable_2: resolveFormulaVariableId(_, 'formula_variable_2'),
				minValue: _.min_value ?? undefined,
				maxValue: _.max_value ?? undefined,
				dataframeDataId: _.id,
				...(factFilter && { operator: factFilter.operator }),
				...(factFilter && { value: factFilter.value }),
			},
			entityType: OptionsBuilderItemTypes.KeyMeasureFact,
			itemType: 'dropdown',
			allowedZones: [DataBuilderZones.facts],
			static: false,
			icon: 'database',
		});
	});

	return factsToSet;
};

export const populateDroppedRowsByDataframe = (
	dataframe: TDataframe,
	dimensions: TDimension[]
): DraggableMenuItemData[] => {
	const dimsToSet: DraggableMenuItemData[] = [];

	dataframe.rowEntry.forEach((_: TDataframeEntry) => {
		let dimToUse: TDimension | TDimensionAttribute | undefined = undefined;

		if (
			_.entity_type === OptionsBuilderItemTypes.Dimension ||
			_.entity_type === OptionsBuilderItemTypes.DateSeries
		) {
			dimToUse = dimensions.find((dim) => dim.id === _.entity_id);

			dimsToSet.push({
				id: generateGUID(),
				entity: dimToUse,
				data: {
					id: _.entity_id,
					title: _.title ?? '',
					drillable: _.drillable,
					isVisible: true,
					totaled: _.totaled,
					...(_.entity_type === OptionsBuilderItemTypes.DateSeries && {
						previousPeriods: _.previous_periods,
					}),
					...(_.entity_type === OptionsBuilderItemTypes.DateSeries && {
						futurePeriods: _.future_periods,
					}),
					useSequence: _.use_sequence,
				},
				entityType: _.entity_type,
				itemType: 'dropdown',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
				icon: 'dataSource',
			});
		} else if (_.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
			dimensions.forEach((dim) => {
				const dimAttr = dim.dimensionAttributes.find((attr) => attr.id === _.entity_id);
				if (dimAttr) {
					dimToUse = dimAttr;
				}
			});

			dimsToSet.push({
				id: generateGUID(),
				entity: dimToUse,
				data: {
					id: _.entity_id,
					title: _.title ?? '',
					drillable: _.drillable,
					isVisible: true,
					totaled: _.totaled,
					useSequence: _.use_sequence,
				},
				entityType: _.entity_type,
				itemType: 'dropdown',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
				icon: 'dataSource',
			});
		}
	});

	return dimsToSet;
};

export const populateDroppedColumnsByDataframe = (
	dataframe: TDataframe,
	dimensions: TDimension[]
): DraggableMenuItemData[] => {
	const dimsToSet: DraggableMenuItemData[] = [];

	dataframe.columnEntry.forEach((_: TDataframeEntry) => {
		let dimToUse: TDimension | TDimensionAttribute | undefined = undefined;

		if (
			_.entity_type === OptionsBuilderItemTypes.Dimension ||
			_.entity_type === OptionsBuilderItemTypes.DateSeries
		) {
			dimToUse = dimensions.find((dim) => dim.id === _.entity_id);

			dimsToSet.push({
				id: generateGUID(),
				entity: dimToUse,
				data: {
					id: _.entity_id,
					title: _.title ?? '',
					drillable: _.drillable,
					isVisible: true,
					totaled: _.totaled,
					...(_.entity_type === OptionsBuilderItemTypes.DateSeries && {
						previousPeriods: _.previous_periods,
					}),
					...(_.entity_type === OptionsBuilderItemTypes.DateSeries && {
						futurePeriods: _.future_periods,
					}),
					useSequence: _.use_sequence,
				},
				entityType: _.entity_type,
				itemType: 'dropdown',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
				],
				static: false,
				icon: 'dataSource',
			});
		} else if (_.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
			dimensions.forEach((dim) => {
				const dimAttr = dim.dimensionAttributes.find((attr) => attr.id === _.entity_id);
				if (dimAttr) {
					dimToUse = dimAttr;
				}
			});

			dimsToSet.push({
				id: generateGUID(),
				entity: dimToUse,
				data: {
					id: _.entity_id,
					title: _.title ?? '',
					drillable: _.drillable,
					isVisible: true,
					totaled: _.totaled,
					useSequence: _.use_sequence,
				},
				entityType: _.entity_type,
				itemType: 'dropdown',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
				],
				static: false,
				icon: 'dataSource',
			});
		}
	});

	return dimsToSet;
};

export const populateDroppedFiltersByDashboardFilters = (
	sharedFilters: DashboardFilter[],
	dataframe: TDataframe,
	dimensions: TDimension[],
	displayIcon: boolean
): DraggableMenuItemData[] => {
	const filtersToSet: DraggableMenuItemData[] = [];
	const existingFilters = populateDroppedFiltersByDataframe(dataframe, dimensions, displayIcon);

	existingFilters.forEach((existingFilter) => {
		const commonFilter = sharedFilters.find(
			(filter) =>
				filter.entity_type === existingFilter.entityType &&
				filter.entity_id === existingFilter.data?.id
		);

		if (commonFilter) {
			if (existingFilter.data) {
				(existingFilter.data.excluded = commonFilter.excluded),
					(existingFilter.data.operator = commonFilter.operator),
					(existingFilter.data.isExistingValue = commonFilter.isExistingValue),
					(existingFilter.data.value = commonFilter.value);
			}
		}

		filtersToSet.push(existingFilter);
	});

	sharedFilters
		.filter((filter) => {
			return !existingFilters.some(
				(existingFilter) =>
					existingFilter.entityType === filter.entity_type &&
					existingFilter.data?.id === filter.entity_id
			);
		})
		.forEach((_) => {
			let dimToUse: TDimension | TDimensionAttribute | undefined = undefined;

			if (_.entity_type === OptionsBuilderItemTypes.Dimension) {
				dimToUse = dimensions.find((dim) => dim.id === _.entity_id);

				filtersToSet.push({
					id: generateGUID(),
					entity: dimToUse,
					data: {
						id: _.entity_id,
						title: dimToUse ? dimToUse.name : '',
						excluded: _.excluded,
						operator: _.operator,
						isExistingValue: _.isExistingValue,
						value: _.value,
						isVisible: true,
						totaled: false,
						showSetFilterIcon: displayIcon && !isUndefined(_.value),
						disabled: displayIcon && !isUndefined(_.value),
					},
					entityType: _.entity_type,
					itemType: 'button',
					allowedZones: [
						DataBuilderZones.rows,
						DataBuilderZones.columns,
						DataBuilderZones.filters,
						DataBuilderZones.drills,
					],
					static: false,
				});
			} else if (_.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
				dimensions.forEach((dim) => {
					const dimAttr = dim.dimensionAttributes.find((attr) => attr.id === _.entity_id);
					if (dimAttr) {
						dimToUse = dimAttr;
					}
				});

				filtersToSet.push({
					id: generateGUID(),
					entity: dimToUse,
					data: {
						id: _.entity_id,
						title: dimToUse ? dimToUse['name'] : '',
						excluded: _.excluded,
						operator: _.operator,
						isExistingValue: _.isExistingValue,
						value: _.value,
						isVisible: true,
						totaled: false,
						showSetFilterIcon: displayIcon && !isUndefined(_.value),
						disabled: displayIcon && !isUndefined(_.value),
					},
					entityType: _.entity_type,
					itemType: 'button',
					allowedZones: [
						DataBuilderZones.rows,
						DataBuilderZones.columns,
						DataBuilderZones.filters,
						DataBuilderZones.drills,
					],
					static: false,
				});
			}
		});

	return filtersToSet;
};

export const populateDroppedFiltersByDataframe = (
	dataframe: TDataframe,
	dimensions: TDimension[],
	displayIcon?: boolean
): DraggableMenuItemData[] => {
	const dimsToSet: DraggableMenuItemData[] = [];

	dataframe.filters.forEach((_: TDataframeFilter) => {
		let dimToUse: TDimension | TDimensionAttribute | undefined = undefined;

		if (_.entity_type === OptionsBuilderItemTypes.Dimension) {
			dimToUse = dimensions.find((dim) => dim.id === _.entity_id);

			dimsToSet.push({
				id: generateGUID(),
				entity: dimToUse,
				data: {
					id: _.entity_id,
					title: dimToUse ? dimToUse.name : '',
					excluded: _.excluded,
					operator: _.operator,
					isExistingValue: _.isExistingValue,
					value: _.value,
					isVisible: true,
					totaled: false,
					showSetFilterIcon: displayIcon && !isUndefined(_.value),
					disabled: displayIcon && !isUndefined(_.value),
				},
				entityType: _.entity_type,
				itemType: 'button',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
			});
		} else if (_.entity_type === OptionsBuilderItemTypes.DimensionAttribute) {
			dimensions.forEach((dim) => {
				const dimAttr = dim.dimensionAttributes.find((attr) => attr.id === _.entity_id);
				if (dimAttr) {
					dimToUse = dimAttr;
				}
			});

			dimsToSet.push({
				id: generateGUID(),
				entity: dimToUse,
				data: {
					id: _.entity_id,
					title: dimToUse ? dimToUse['name'] : '',
					excluded: _.excluded,
					operator: _.operator,
					isExistingValue: _.isExistingValue,
					value: _.value,
					isVisible: true,
					totaled: false,
					showSetFilterIcon: displayIcon && !isUndefined(_.value),
					disabled: displayIcon && !isUndefined(_.value),
				},
				entityType: _.entity_type,
				itemType: 'button',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
			});
		}
	});

	return dimsToSet;
};

export const populateDroppedFactsByKeyMeasure = (
	measureId: number,
	measureFactId: number,
	measures: TKeyMeasure[],
	unitTypes?: TUnitType[]
): DraggableMenuItemData[] => {
	const factsToSet: DraggableMenuItemData[] = [];

	const measure = measures.find((measure) => measure.id === measureId);

	if (measure) {
		const fact = measure.keyMeasureFacts.find((fact) => fact.id == measureFactId);

		if (fact) {
			let unitType = undefined;
			if (unitTypes) {
				unitType = unitTypes.find((u) => u.id === fact.unitType);
			}
			factsToSet.push({
				id: generateGUID(),
				entity: fact,
				data: populateDataForFact(fact, unitType),
				entityType: OptionsBuilderItemTypes.KeyMeasureFact,
				itemType: 'dropdown',
				allowedZones: [DataBuilderZones.facts],
				static: false,
				icon: 'database',
			});
		}
	}

	return factsToSet;
};

export const populateDroppedRowsByKeyMeasure = (
	dimensions: TDimension[],
	dimensionId?: number,
	dimensionAttributeId?: number
): DraggableMenuItemData[] => {
	const dimsToSet: DraggableMenuItemData[] = [];

	if (!dimensionId && !dimensionAttributeId) {
		dimsToSet.push({
			id: generateGUID(),
			entity: { id: 0 },
			data: {
				id: 0,
				title: 'Date Range',
				useSequence: false,
				drillable: false,
				isVisible: true,
				totaled: false,
			},
			entityType: OptionsBuilderItemTypes.DateSeries,
			itemType: 'date',
			allowedZones: [
				DataBuilderZones.rows,
				DataBuilderZones.columns,
				DataBuilderZones.filters,
				DataBuilderZones.drills,
			],
			static: false,
			icon: 'dataSource',
		});
	} else if (dimensionId) {
		const dim = dimensions.find((dim) => dim.id === dimensionId);
		if (dim) {
			dimsToSet.push({
				id: generateGUID(),
				entity: dim,
				data: populateDataForDimension(dim),
				entityType: OptionsBuilderItemTypes.Dimension,
				itemType: 'dropdown',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
				icon: 'dataSource',
			});
		}
	} else if (dimensionAttributeId) {
		const dim = dimensions.find((dim) =>
			dim.dimensionAttributes.some((dimAttr) => dimAttr.id === dimensionAttributeId)
		);

		if (dim) {
			const dimAttr = dim.dimensionAttributes.find(
				(dimAtt) => dimAtt.id === dimensionAttributeId
			);

			if (dimAttr) {
				dimsToSet.push({
					id: generateGUID(),
					entity: dimAttr,
					data: populateDataForDimensionAttribute(dimAttr),
					entityType: OptionsBuilderItemTypes.DimensionAttribute,
					itemType: 'dropdown',
					allowedZones: [
						DataBuilderZones.rows,
						DataBuilderZones.columns,
						DataBuilderZones.filters,
						DataBuilderZones.drills,
					],
					static: false,
					icon: 'dataSource',
				});
			}
		}
	}

	return dimsToSet;
};

export const populateDroppedFactsByKeyMeasureFact = (
	measureFactId: number,
	measures: TKeyMeasure[],
	unitTypes?: TUnitType[]
): DraggableMenuItemData[] => {
	const factsToSet: DraggableMenuItemData[] = [];

	const measuresToUse = measures.filter((measure: TKeyMeasure) => {
		return measure.keyMeasureFacts.some((fact: TKeyMeasureFact) => {
			return fact.id === measureFactId;
		});
	});

	const keyMeasureFactsToUse = measuresToUse.flatMap((measure) => measure.keyMeasureFacts);

	keyMeasureFactsToUse.forEach((fact) => {
		let unitType = undefined;
		if (unitTypes) {
			unitType = unitTypes.find((u) => u.id === fact.unitType);
		}
		factsToSet.push({
			id: generateGUID(),
			entity: fact,
			data: populateDataForFact(fact, unitType),
			entityType: OptionsBuilderItemTypes.KeyMeasureFact,
			itemType: 'dropdown',
			allowedZones: [DataBuilderZones.facts],
			static: false,
			icon: 'database',
		});
	});

	return factsToSet;
};

export const populateDroppedRowsByKeyMeasureFact = (
	measureFactId: number,
	measures: TKeyMeasure[],
	dimensions: TDimension[]
): DraggableMenuItemData[] => {
	const dimsToSet: DraggableMenuItemData[] = [];

	const measuresToUse = measures.filter((measure: TKeyMeasure) => {
		return measure.keyMeasureFacts.some((fact: TKeyMeasureFact) => {
			return fact.id === measureFactId;
		});
	});

	const measureIds = measuresToUse.map((measure: TKeyMeasure) => measure.id);

	dimensions.map((dimension: TDimension): void => {
		dimension.keyMeasures.map((measure: TKeyMeasure): void => {
			if (measureIds.includes(measure.id)) {
				dimsToSet.push({
					id: generateGUID(),
					entity: dimension,
					data: populateDataForDimension(dimension),
					entityType: OptionsBuilderItemTypes.Dimension,
					itemType: 'dropdown',
					allowedZones: [
						DataBuilderZones.rows,
						DataBuilderZones.columns,
						DataBuilderZones.filters,
						DataBuilderZones.drills,
					],
					static: false,
					icon: 'dataSource',
				});

				if (dimension.dimensionAttributes.length) {
					dimension.dimensionAttributes.forEach((dimAttr) => {
						dimsToSet.push({
							id: generateGUID(),
							entity: dimAttr,
							data: populateDataForDimensionAttribute(dimAttr),
							entityType: OptionsBuilderItemTypes.DimensionAttribute,
							itemType: 'dropdown',
							allowedZones: [
								DataBuilderZones.rows,
								DataBuilderZones.columns,
								DataBuilderZones.filters,
								DataBuilderZones.drills,
							],
							static: false,
							icon: 'dataSource',
						});
					});
				}
			}
		});
	});

	return dimsToSet;
};

export const populateDroppedFilterByDimension = (
	id: number,
	dimensions: TDimension[]
): DraggableMenuItemData | null => {
	let dimToSet: DraggableMenuItemData | null = null;

	const dim = dimensions.find((dim) => dim.id === id);

	if (dim) {
		dimToSet = {
			id: generateGUID(),
			entity: dim,
			data: populateDataForDimension(dim),
			entityType: OptionsBuilderItemTypes.Dimension,
			itemType: 'button',
			allowedZones: [
				DataBuilderZones.rows,
				DataBuilderZones.columns,
				DataBuilderZones.filters,
				DataBuilderZones.drills,
			],
			static: false,
		};
	}

	return dimToSet;
};

export const populateDroppedFilterByDimensionAttribute = (
	id: number,
	dimensions: TDimension[]
): DraggableMenuItemData | null => {
	let dimToSet: DraggableMenuItemData | null = null;

	dimensions.forEach((dim) => {
		const attr = dim.dimensionAttributes.find((attr) => attr.id === id);
		if (attr) {
			dimToSet = {
				id: generateGUID(),
				entity: attr,
				data: populateDataForDimensionAttribute(attr),
				entityType: OptionsBuilderItemTypes.DimensionAttribute,
				itemType: 'button',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
			};
		}
	});

	return dimToSet;
};

export const populateDroppedRowByDimension = (
	id: number,
	dimensions: TDimension[]
): DraggableMenuItemData | null => {
	let dimToSet: DraggableMenuItemData | null = null;

	const dim = dimensions.find((dim) => dim.id === id);

	if (dim) {
		dimToSet = {
			id: generateGUID(),
			entity: dim,
			data: populateDataForDimension(dim),
			entityType: OptionsBuilderItemTypes.Dimension,
			itemType: 'dropdown',
			allowedZones: [
				DataBuilderZones.rows,
				DataBuilderZones.columns,
				DataBuilderZones.filters,
				DataBuilderZones.drills,
			],
			static: false,
			icon: 'dataSource',
		};
	}

	return dimToSet;
};

export const populateDroppedRowByDimensionAttribute = (
	id: number,
	dimensions: TDimension[]
): DraggableMenuItemData | null => {
	let dimToSet: DraggableMenuItemData | null = null;

	dimensions.forEach((dim) => {
		const attr = dim.dimensionAttributes.find((attr) => attr.id === id);
		if (attr) {
			dimToSet = {
				id: generateGUID(),
				entity: attr,
				data: populateDataForDimensionAttribute(attr),
				entityType: OptionsBuilderItemTypes.DimensionAttribute,
				itemType: 'dropdown',
				allowedZones: [
					DataBuilderZones.rows,
					DataBuilderZones.columns,
					DataBuilderZones.filters,
					DataBuilderZones.drills,
				],
				static: false,
				icon: 'dataSource',
			};
		}
	});

	return dimToSet;
};

export const populateDefaultDatRangeDim = (): DraggableMenuItemData => {
	return {
		id: generateGUID(),
		entity: { id: 0 },
		data: {
			id: 0,
			title: 'Date Range',
			useSequence: false,
			drillable: false,
			isVisible: true,
			totaled: false,
		},
		entityType: OptionsBuilderItemTypes.DateSeries,
		itemType: 'dropdown',
		allowedZones: [DataBuilderZones.rows, DataBuilderZones.columns, DataBuilderZones.filters],
		static: false,
		icon: 'dataSource',
	};
};

const getExistingFactId = (
	dataframe: TDataframe,
	item: DraggableMenuItemData
): number | undefined => {
	const fact = item.data?.calculated
		? dataframe?.datasets.find((set): boolean => set.id === item.data?.dataframeDataId)
		: dataframe?.datasets.find((set): boolean => set.keyMeasureFact === item.data?.id);

	if (fact) {
		return fact.id;
	}
};

const getExistingRowId = (
	dataframe: TDataframe,
	item: DraggableMenuItemData
): number | undefined => {
	const field = dataframe?.rowEntry.find(
		(set): boolean => set.entity_id === item.data?.id && set.entity_type === item.entityType
	);
	if (field) {
		return field.id;
	}
};

const getExistingColumnId = (
	dataframe: TDataframe,
	item: DraggableMenuItemData
): number | undefined => {
	const field = dataframe?.columnEntry.find(
		(set): boolean => set.entity_id === item.data?.id && set.entity_type === item.entityType
	);
	if (field) {
		return field.id;
	}
};

const getExistingFilterId = (
	dataframe: TDataframe,
	item: DraggableMenuItemData
): number | undefined => {
	const filter = dataframe?.filters.find(
		(set): boolean => set.entity_id === item.data?.id && set.entity_type === item.entityType
	);
	if (filter) {
		return filter.id;
	}
};

export const getUnitTypeIdByFactId = (measures: KeyMeasure[], factId: number) => {
	let fact: KeyMeasureFact | undefined;
	measures.forEach((measure) => {
		const factToUse = measure.keyMeasureFacts.find((f) => f.id === factId);
		if (factToUse) {
			fact = factToUse;
		}
	});

	if (fact) {
		return fact.unitType as number;
	}
};

export const buildDataframeRequest = (
	facts: DraggableMenuItemData[],
	rows: DraggableMenuItemData[],
	columns: DraggableMenuItemData[],
	filters: DraggableMenuItemData[],
	name: string,
	order: (TDataframeOrder | TNewDataframeOrder)[],
	isChild: boolean,
	isPatch: boolean,
	existingDataframe?: TDataframe,
	folder?: TFolder,
	managed?: boolean,
	dataLimit?: number
): TDataframe | TNewDataframe => {
	return {
		...(existingDataframe && isPatch && { id: existingDataframe.id }),
		parent: isChild ? existingDataframe?.id : existingDataframe?.parent,
		name: name != '' ? name : MetaDataFrame.Name + generateRandomString(5),
		datasets: facts.map((fact: DraggableMenuItemData): TNewDataframeData | TDataframeData => {
			return {
				...(existingDataframe &&
					isPatch &&
					getExistingFactId(existingDataframe, fact) && {
						id: getExistingFactId(existingDataframe, fact),
					}),
				keyMeasureFact: fact.data?.calculated ? 0 : fact.data?.id,
				title: fact.data?.title ?? '',
				numDecimals: fact.data?.numDecimals ?? 0,
				hidden: fact.data?.hidden ?? false,
				unitType: fact.data?.unitType ?? 0,
				alias: fact.data?.alias,
				calculated: fact.data?.calculated,
				formula_operation: fact.data?.formula_operation,
				formula_variable_1: fact.data?.formula_variable_1,
				formula_variable_2: fact.data?.formula_variable_2,
				sequence: fact.data?.sequence,
				defaultWhenNULL: fact.data?.defaultWhenNull ?? '***',
				min_value: fact.data?.minValue ?? null,
				max_value: fact.data?.maxValue ?? null,
				data_limit: dataLimit ?? 0,
			};
		}),
		folder: folder?.id,
		rowEntry: rows.map((row: DraggableMenuItemData): TNewDataframeEntry | TDataframeEntry => {
			return {
				...(existingDataframe &&
					isPatch &&
					getExistingRowId(existingDataframe, row) && {
						id: getExistingRowId(existingDataframe, row),
					}),
				entity_type: row.entityType,
				entity_id: row.data?.id ?? 0,
				title: row.data?.title ?? '',
				drillable: row.data?.drillable ?? false,
				totaled: row.data?.totaled ?? false,
				...(row.entityType === OptionsBuilderItemTypes.DateSeries && {
					previous_periods: row.data?.previousPeriods,
				}),
				...(row.entityType === OptionsBuilderItemTypes.DateSeries && {
					future_periods: row.data?.futurePeriods,
				}),
				use_sequence: row.data?.useSequence ?? false,
			};
		}),
		columnEntry: columns.map(
			(column: DraggableMenuItemData): TNewDataframeEntry | TDataframeEntry => {
				return {
					...(existingDataframe &&
						isPatch &&
						getExistingColumnId(existingDataframe, column) && {
							id: getExistingColumnId(existingDataframe, column),
						}),
					entity_type: column.entityType,
					entity_id: column.data?.id ?? 0,
					title: column.data?.title ?? '',
					drillable: column.data?.drillable ?? false,
					totaled: column.data?.totaled ?? false,
					...(column.entityType === OptionsBuilderItemTypes.DateSeries && {
						previous_periods: column.data?.previousPeriods,
					}),
					...(column.entityType === OptionsBuilderItemTypes.DateSeries && {
						future_periods: column.data?.futurePeriods,
					}),
					use_sequence: column.data?.useSequence ?? false,
				};
			}
		),
		filters: [
			...filters
				.filter((filter) => filter.entityType != OptionsBuilderItemTypes.DatePeriodSelector)
				.map((filter: DraggableMenuItemData): TNewDataframeFilter | TDataframeFilter => {
					return {
						...(existingDataframe &&
							isPatch &&
							getExistingFilterId(existingDataframe, filter) && {
								id: getExistingFilterId(existingDataframe, filter),
							}),
						entity_type: filter.entityType,
						entity_id: filter.data?.id ?? 0,
						...(filter.data?.value && { value: filter.data.value }),
						excluded: filter.data?.excluded ?? false,
						isExistingValue: filter.data?.isExistingValue ?? false,
						...(filter.data?.operator && { operator: filter.data.operator }),
					};
				}),
			...facts
				.filter(
					(fact: DraggableMenuItemData): boolean =>
						fact.data?.operator != void 0 && fact.data.value != void 0
				)
				.map((fact: DraggableMenuItemData): TNewDataframeFilter | TDataframeFilter => {
					return {
						...(existingDataframe &&
							isPatch &&
							getExistingFilterId(existingDataframe, fact) && {
								id: getExistingFilterId(existingDataframe, fact),
							}),
						entity_type: fact.entityType,
						entity_id: fact.data?.calculated
							? fact.data?.dataframeDataId ?? 0
							: fact.data?.id ?? 0,
						...(fact.data?.value && { value: fact.data.value }),
						excluded: false,
						isExistingValue: false,
						...(fact.data?.operator && { operator: fact.data.operator }),
					};
				}),
		],
		order: order.map((o) => {
			if (o.entity_type === OptionsBuilderItemTypes.CalculatedFact && !o.entity_id) {
				const matchingFact = facts.find(
					(f) => f.data?.calculated && f.data?.dataframeDataId
				);
				if (matchingFact) {
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					return { ...o, entity_id: matchingFact.data!.dataframeDataId! };
				}
			}
			return o;
		}),
		managed: existingDataframe && isPatch ? existingDataframe.managed : managed ?? false,
	};
};

export const getEffectiveStaticFilters = (
	dataframe: TDataframe,
	parentDataframe?: TDataframe
): TNewDataframeFilter[] => {
	const filtersByKey = new Map<string, TNewDataframeFilter>();

	const addFilters = (filterList?: TDataframeFilter[]) => {
		filterList?.forEach((filter) => {
			filtersByKey.set(`${filter.entity_type}|${filter.entity_id}`, filter);
		});
	};

	addFilters(parentDataframe?.filters);
	addFilters(dataframe.filters);

	return Array.from(filtersByKey.values());
};

export const setAvailableMeasuresAndDimensionsByDataframe = (
	dataframe: TDataframe | undefined,
	parentDataframe: TDataframe | undefined,
	measures: TKeyMeasure[],
	dimensions: TDimension[],
	limitDimensionOptions: boolean
): AvailableItems => {
	const measuresToUse: TKeyMeasure[] = [];
	let dimsToUse: TDimension[] = [defaultDateRangeDim];

	if (dataframe) {
		// Populate selected measure with selected facts
		let factIds: (number | undefined)[] = [];
		if (parentDataframe) {
			factIds = parentDataframe?.datasets.flatMap((fact) =>
				fact.calculated
					? [fact.formula_variable_1, fact.formula_variable_2]
					: [fact.keyMeasureFact]
			);
		} else {
			factIds = dataframe?.datasets.flatMap((fact) =>
				fact.calculated
					? [fact.formula_variable_1, fact.formula_variable_2]
					: [fact.keyMeasureFact]
			);
		}

		measures.forEach((measure: TKeyMeasure) => {
			const clonedMeasure = _.cloneDeep(measure);
			clonedMeasure.keyMeasureFacts = clonedMeasure.keyMeasureFacts.filter(
				(fact: TKeyMeasureFact) => {
					return factIds && factIds.includes(fact.id);
				}
			);
			if (clonedMeasure.keyMeasureFacts.length > 0) {
				measuresToUse.push(clonedMeasure);
			}
		});

		if (limitDimensionOptions) {
			// Populate selected dimensions and selected dimension attributes based on dataframe row entries, column entries and filters
			let dimensionIds: number[] = [];
			let dimensionAttrIds: number[] = [];

			dimensionIds = [
				...(parentDataframe ?? dataframe).rowEntry
					.filter((row) => {
						return row.entity_type == OptionsBuilderItemTypes.Dimension;
					})
					.map((dim) => dim.entity_id),
				...(parentDataframe ?? dataframe).columnEntry
					.filter((col) => {
						return col.entity_type == OptionsBuilderItemTypes.Dimension;
					})
					.map((dim) => dim.entity_id),
				...(parentDataframe ?? dataframe).filters
					.filter((filter) => {
						return filter.entity_type == OptionsBuilderItemTypes.Dimension;
					})
					.map((dim) => dim.entity_id),
			];

			dimensionAttrIds = [
				...(parentDataframe ?? dataframe).rowEntry
					.filter((row) => {
						return row.entity_type == OptionsBuilderItemTypes.DimensionAttribute;
					})
					.map((dim) => dim.entity_id),
				...(parentDataframe ?? dataframe).columnEntry
					.filter((col) => {
						return col.entity_type == OptionsBuilderItemTypes.DimensionAttribute;
					})
					.map((dim) => dim.entity_id),
				...(parentDataframe ?? dataframe).filters
					.filter((filter) => {
						return filter.entity_type == OptionsBuilderItemTypes.DimensionAttribute;
					})
					.map((dim) => dim.entity_id),
			];

			// Remove duplicate Ids
			dimensionIds = uniq(dimensionIds);
			dimensionAttrIds = uniq(dimensionAttrIds);

			dimensions.forEach((dimension: TDimension): void => {
				const clonedDim = _.cloneDeep(dimension);

				if (
					dimensionIds.includes(clonedDim.id) ||
					clonedDim.dimensionAttributes.some((attr: TDimensionAttribute) =>
						dimensionAttrIds.includes(attr.id)
					)
				) {
					clonedDim.dimensionAttributes = clonedDim.dimensionAttributes.filter(
						(attr: TDimensionAttribute) => dimensionAttrIds?.includes(attr.id)
					);
					dimsToUse.push(clonedDim);
				}
			});
		} else {
			dimsToUse = [
				defaultDateRangeDim,
				...dimensions.filter((dimension) => {
					return dimension.keyMeasures.some((measure) => {
						return measuresToUse.map((measure) => measure.id).includes(measure.id);
					});
				}),
			];
		}
	}

	return {
		measures: measuresToUse,
		dimensions: dimsToUse,
	};
};
